import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import PriceHistory from '../components/PriceHistory';
import AdditionalCosts from '../components/AdditionalCosts';
import PropertyDetail from '../components/PropertyDetail';
import PropertyFeature from '../components/PropertyFeature';
import { useSearch } from '../contexts/SearchContext';
import { supabase } from '../lib/supabase';
import { usePropertyTypesAndFeatures } from '../hooks/usePropertyTypesAndFeatures';
import { usePropertyFields } from '../hooks/usePropertyFields';
import { formatInvestmentAddress } from '../utils/addressFormatter';
import { PriceField } from '../config/propertyFields/PriceField';
import { LendiCalculator } from '../components/LendiCalculator';
import { LendiTeaserButton, LendiTeaserPanel } from '../components/LendiInstallmentTeaser';
import { getParentOrigin } from '../utils/iframeMessenger';
import { DEEP_LINK_PARAM } from '../config/deepLink';
import { useWidgetEvent } from '../contexts/WidgetEventContext';
import './PropertyDetails.css';

const priceField = new PriceField();

// Local image assets
const imgWarstwa2 = "/assets/b84f673efb2755831605b51c2f49f9316c492920.svg";
const imgWarstwa3 = "/assets/6957e84846200bfa1dd67512ed53f4f84dbba04b.svg";
const imgWarstwa4 = "/assets/bfd6b9e32a114a968ffbb4503978e350d39daf84.svg";

export default function PropertyDetails() {
  const { id: encodedId } = useParams();
  const id = decodeURIComponent(encodedId);
  const navigate = useNavigate();
  const [showHistory, setShowHistory] = useState(false);
  const [showAdditionalCosts, setShowAdditionalCosts] = useState(false);
  const [lendiExpanded, setLendiExpanded] = useState(false);
  const { lendiPending, consumeLendiPending } = useWidgetEvent();
  const [propertyMedia, setPropertyMedia] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const { properties, optionalUnits, investmentData, isMultiInvestment, loading, error } = useSearch();
  const { getPropertyTypeLabel } = usePropertyTypesAndFeatures();
  const { fieldConfigs, renderPropertyField, getFieldIcon } = usePropertyFields(investmentData, 'card');

  // Find property by ID (which could be UUID or slug)
  const property = properties.find(p => {
    // Try exact UUID match
    if (p.id === id) {
      return true;
    }
    
    // Try slug match if exists
    if (p.slug === id) {
      return true;
    }
    
    // Try title-based matching
    const titleSlug = p.title.toLowerCase().replace(/\s+/g, '-');
    if (titleSlug === id) {
      return true;
    }
    
    // Try matching with "5a" format
    const cleanTitle = p.title.toLowerCase();
    const cleanId = id.replace(/[-_]/g, '').toLowerCase();
    if (cleanTitle === cleanId) {
      return true;
    }
    
    return false;
  });

  // Load property media when property is found
  useEffect(() => {
    if (property) {
      loadPropertyMedia(property);
    }
  }, [property]);

  // Scroll to top when component mounts or property ID changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  // Consume pending lendi-expand request from event bus (URL or runtime postMessage)
  useEffect(() => {
    if (lendiPending) {
      setLendiExpanded(true);
      consumeLendiPending();
    }
  }, [lendiPending, consumeLendiPending]);

  const loadPropertyMedia = async (propertyData) => {
    setMediaLoading(true);
    try {
      // First, get the actual property record from database to get its UUID
      // Filter by investment to avoid duplicates with same external_id
      const { data: propertyRecord, error: propertyError } = await supabase
        .from('properties')
        .select(`
          id,
          stage:stages!inner(
            investment:investments!inner(id, slug)
          )
        `)
        .eq('external_id', propertyData.id)
        .eq('stage.investment.slug', investmentData?.slug)
        .single();

      if (propertyError) {
        console.error('Error finding property:', propertyError);
        setPropertyMedia([]);
        return;
      }

      // Now load media using the database property ID
      const { data: mediaData, error: mediaError } = await supabase
        .from('property_medias')
        .select('id, file_url, alt_text, title')
        .eq('property_id', propertyRecord.id)
        .eq('is_active', true)
        .eq('type', 'image')
        .order('sort_order');

      if (mediaError) {
        console.error('Error loading property media:', mediaError);
        setPropertyMedia([]);
        return;
      }

      setPropertyMedia(mediaData || []);
      setCurrentImageIndex(0);
    } catch (error) {
      console.error('Error loading property media:', error);
      setPropertyMedia([]);
    } finally {
      setMediaLoading(false);
    }
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? propertyMedia.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === propertyMedia.length - 1 ? 0 : prev + 1
    );
  };

  // Image gallery navigation (using arrows/dots only)

  const getCurrentImage = () => {
    if (propertyMedia.length > 0 && propertyMedia[currentImageIndex]) {
      return propertyMedia[currentImageIndex];
    }
    // Fallback to original media from property object
    return {
      file_url: property?.media?.main_image,
      alt_text: property?.media?.main_image_alt || 'Property image'
    };
  };

  // Filter properties by the same propertyType for navigation (type-agnostic)
  const sameTypeProperties = property
    ? properties.filter(p => p.propertyType === property.propertyType)
    : [];

  const handleNavigateToPrevious = () => {
    if (sameTypeProperties.length <= 1) return;
    const currentIndex = sameTypeProperties.findIndex(p => p.id === property.id);
    // Circular navigation: if at start, go to last
    const prevIndex = currentIndex <= 0
      ? sameTypeProperties.length - 1
      : currentIndex - 1;
    const prevProperty = sameTypeProperties[prevIndex];
    navigate(`/properties/${encodeURIComponent(prevProperty.id)}`);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const handleNavigateToNext = () => {
    if (sameTypeProperties.length <= 1) return;
    const currentIndex = sameTypeProperties.findIndex(p => p.id === property.id);
    // Circular navigation: if at end, go to first
    const nextIndex = currentIndex >= sameTypeProperties.length - 1
      ? 0
      : currentIndex + 1;
    const nextProperty = sameTypeProperties[nextIndex];
    navigate(`/properties/${encodeURIComponent(nextProperty.id)}`);
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const handleBack = () => {
    navigate('/');
  };


  const toggleHistory = () => {
    setShowHistory(!showHistory);
    if (!showHistory) {
      setShowAdditionalCosts(false);
    }
  };

  const toggleAdditionalCosts = () => {
    setShowAdditionalCosts(!showAdditionalCosts);
    if (!showAdditionalCosts) {
      setShowHistory(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const propertyTypeLabel = property ? (property.propertyTypeLabel || getPropertyTypeLabel(property.propertyType)) : 'Lokal mieszkalny';
  const fullPropertyTitle = property ? `${propertyTypeLabel} ${property.title}` : '';

  // Filter optional units to only show those from the same stage as the current property
  // For multi-investment setups, show all optional units (they may come from different investments)
  // Properties with parking_group have mandatory additional costs instead of optional units
  const filteredOptionalUnits = (() => {
    if (!property) return optionalUnits || [];

    // If property has a parking_group, hide optional units
    // (they have mandatory additional costs for parking instead)
    if (property.parkingGroup) {
      return [];
    }

    // For multi-investment setups, show all optional units
    if (isMultiInvestment) {
      return optionalUnits || [];
    }

    // For single investment, filter by stage
    if (property.stage?.id) {
      return (optionalUnits || []).filter(unit => unit.stage?.id === property.stage.id);
    }

    return optionalUnits || [];
  })();

  const handleWhatsAppContact = () => {
    if (property?.contact?.agent_whatsapp) {
      const phoneNumber = property.contact.agent_whatsapp.replace(/\s+/g, '').replace(/\+/g, '');
      const message = encodeURIComponent(`Witam! Interesuje mnie ${fullPropertyTitle}. Czy mogę otrzymać więcej informacji?`);
      window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    }
  };

  const handlePhoneContact = () => {
    if (property?.contact?.agent_phone) {
      window.open(`tel:${property.contact.agent_phone}`, '_self');
    }
  };

  const handleEmailContact = () => {
    if (property?.contact?.agent_email) {
      const subject = encodeURIComponent(`Zapytanie o ${fullPropertyTitle}`);
      const body = encodeURIComponent(`Witam!\n\nInteresuje mnie ${fullPropertyTitle}.\nCzy mogę otrzymać więcej informacji o tej nieruchomości?\n\nPozdrawiam`);
      window.open(`mailto:${property.contact.agent_email}?subject=${subject}&body=${body}`, '_self');
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie właściwości...</div>;
  }

  if (error) {
    return <div className="error">Błąd: {error}</div>;
  }

  if (!property) {
    return <div className="error">Nie znaleziono właściwości</div>;
  }

  return (
    <div className="property-details-wrapper">
      <header className="property-details-header">
        <div className="header-top-row">
          <button className="back-button" onClick={handleBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Wróć</span>
          </button>
          
          <nav className="breadcrumb">
            <span className="breadcrumb-item">Inwestycja</span>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item">Filtracja</span>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item active">{fullPropertyTitle}</span>
          </nav>
        </div>
        
        <div className="property-header-info">
          <div className={`property-status-badge ${property.status.code}`}>
            <img src={property.status.icon} alt="Status" className="status-icon" />
            <span className="status-text">{property.status.label}</span>
          </div>
          <h1 className="property-title">
            <span className="property-label">{propertyTypeLabel}:</span>
            <span className="property-value">{property.title}</span>
            <button
              className="copy-link-button"
              data-testid="copy-link-button"
              title={linkCopied ? 'Skopiowano!' : 'Kopiuj link do lokalu'}
              onClick={() => {
                const parentOrigin = getParentOrigin();
                const deepLink = parentOrigin
                  ? `${parentOrigin}?${DEEP_LINK_PARAM}=${encodeURIComponent(id)}`
                  : `${window.location.origin}/properties/${encodeURIComponent(id)}`;
                navigator.clipboard.writeText(deepLink).catch(() => {
                  const textarea = document.createElement('textarea');
                  textarea.value = deepLink;
                  document.body.appendChild(textarea);
                  textarea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textarea);
                }).finally(() => {
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                });
              }}
            >
              {linkCopied ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.3 4.3L6 11.6 2.7 8.3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6.7 8.7a3 3 0 004.2.3l2-2a3 3 0 00-4.2-4.3L7.5 3.9M9.3 7.3a3 3 0 00-4.2-.3l-2 2a3 3 0 004.2 4.3L8.5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              )}
            </button>
          </h1>
        </div>
      </header>
      
      <div className="location-section">
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none" className="location-icon">
          <g clipPath="url(#clip0_309_113)">
            <path d="M6.47837 13.0042L6.10079 12.6805C5.58026 12.2447 1.03516 8.31937 1.03516 5.4481C1.03516 2.4419 3.47217 0.00488281 6.47837 0.00488281C9.48457 0.00488281 11.9216 2.4419 11.9216 5.4481C11.9216 8.3194 7.37649 12.2447 6.85812 12.6827L6.47837 13.0042ZM6.47837 1.18187C4.12332 1.18453 2.21483 3.09302 2.21216 5.44807C2.21216 7.25187 5.00848 10.1334 6.47837 11.4523C7.94829 10.1329 10.7446 7.24969 10.7446 5.44807C10.7419 3.09302 8.83346 1.18456 6.47837 1.18187Z" fill="black"/>
            <path d="M6.47796 7.60582C5.28632 7.60582 4.32031 6.6398 4.32031 5.44817C4.32031 4.25654 5.28632 3.29053 6.47796 3.29053C7.66959 3.29053 8.6356 4.25654 8.6356 5.44817C8.6356 6.6398 7.66962 7.60582 6.47796 7.60582ZM6.47796 4.36932C5.88214 4.36932 5.39913 4.85233 5.39913 5.44815C5.39913 6.04396 5.88214 6.52697 6.47796 6.52697C7.07377 6.52697 7.55678 6.04396 7.55678 5.44815C7.55678 4.85233 7.0738 4.36932 6.47796 4.36932Z" fill="black"/>
          </g>
          <defs>
            <clipPath id="clip0_309_113">
              <rect width="13" height="13" fill="white"/>
            </clipPath>
          </defs>
        </svg>
        <span className="location-text">
          <span className="location-label">Lokalizacja:</span>{' '}
          <span className="location-address">{formatInvestmentAddress(investmentData)}</span>
        </span>
      </div>
      
      <div className="location-separator"></div>
      
      <main className="property-details-content">
        {/* Price section - conditional rendering based on status and investment settings */}
        {(() => {
          // Determine if price section should be shown
          const shouldShowPriceSection = () => {
            // Always show for available properties
            if (property.status.code === 'available') return true;
            
            // Check flag for reserved properties
            if (property.status.code === 'reserved') {
              return investmentData?.show_price_for_reserved ?? true; // default true for backward compatibility
            }
            
            // Check flag for sold properties
            if (property.status.code === 'sold') {
              return investmentData?.show_price_for_sold ?? true; // default true for backward compatibility
            }
            
            return true; // Show for any other status
          };

          if (!shouldShowPriceSection()) return null;

          const priceData = priceField.renderForCard(property);

          return (
            <div className="price-section">
              <div className="price-main">
                <div className="price-label">{priceData.label}</div>
                <div className="price-values">
                  {priceData.promo && (
                    <div className="price-value-original">{priceData.promo.originalPrice}</div>
                  )}
                  <div className="price-value">{priceData.value}</div>
                  {priceData.promo?.originalPricePerSqm && (
                    <div className="price-value-original">{priceData.promo.originalPricePerSqm}</div>
                  )}
                  <div className="price-per-meter">{property.pricing.pricePerSquareMeter.format()}</div>
            </div>
            {property.pricing.lastUpdated && (
              <div className="price-last-updated">
                Ceny aktualne na dzień: {formatDate(property.pricing.lastUpdated)}
              </div>
            )}
            {property.pricing?.totalPrice?.value > 0 && property.status.code === 'available' && investmentData?.module_lendi_calculator && (
              <LendiCalculator
                propertyPrice={property.pricing.totalPrice.value}
                collateral={property.propertyType === 'dom' ? 'House' : 'Apartment'}
              />
            )}
          </div>
          <div className="price-toggles">
            <button className="history-toggle" onClick={toggleHistory}>
              <span>{showHistory ? 'Zwiń historię' : 'Zobacz historię'}</span>
              <div className={`history-arrow ${showHistory ? 'expanded' : ''}`}>
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <path d="M3.9 6.05L7.5 9.65L11.1 6.05" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </button>
            <button className="costs-toggle" onClick={toggleAdditionalCosts}>
              <span>{showAdditionalCosts ? 'Zwiń koszty' : 'Dodatkowe koszty'}</span>
              <div className={`history-arrow ${showAdditionalCosts ? 'expanded' : ''}`}>
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <path d="M3.9 6.05L7.5 9.65L11.1 6.05" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </button>
            {property.pricing?.totalPrice?.value > 0 && property.status.code === 'available' && investmentData?.module_lendi_teaser && investmentData.lendi_teaser_embed_html && (
              <LendiTeaserButton
                expanded={lendiExpanded}
                onToggle={() => setLendiExpanded(v => !v)}
              />
            )}
          </div>
        </div>
          );
        })()}

        {/* Price history component */}
        {showHistory && (
          <PriceHistory 
            priceHistory={property.pricing.priceHistory}
            salesStartDate={property.pricing.salesStartDate}
          />
        )}

        {/* Additional costs component */}
        {showAdditionalCosts && (
          <AdditionalCosts 
            additionalCosts={property.pricing.additionalCosts}
            propertyPrice={property.pricing.totalPrice.value}
            parkingSpots={property.parkingSpots}
            parkingPrice={property.parkingPrice}
            parkingIncluded={property.parkingIncludedInPrice}
            optionalUnits={filteredOptionalUnits}
          />
        )}

        {/* Lendi teaser — embed panel */}
        {property.pricing?.totalPrice?.value > 0 && property.status.code === 'available' && investmentData?.module_lendi_teaser && investmentData.lendi_teaser_embed_html && (
          <LendiTeaserPanel
            expanded={lendiExpanded}
            embedHtml={investmentData.lendi_teaser_embed_html}
            onToggle={() => {
              setLendiExpanded(false);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        )}

        {/* Separator line */}
        <div className="separator-line"></div>

        {/* Property features - dynamically configured */}
        <div className="property-features-single-row">
          {fieldConfigs.map(fieldConfig => {
            const result = renderPropertyField(fieldConfig, property);
            if (!result) return null;
            const { label, value } = result;
            return (
              <PropertyDetail
                key={fieldConfig.field.key}
                icon={getFieldIcon(fieldConfig)}
                label={label}
                value={value}
              />
            );
          })}
        </div>

        {/* Separator line */}
        <div className="separator-line"></div>

        {/* Additional features section */}
        <div className="additional-features">
          <div className="additional-features-row">
            {(property.specifications?.features?.all || []).map(feature => (
              <PropertyFeature
                key={feature.key}
                iconSvg={feature.icon_svg}
                label={feature.label}
              />
            ))}
          </div>
        </div>

        {/* Separator line */}
        <div className="separator-line"></div>

        {/* Image section */}
        <div className="apartment-image-section">
          <div className="apartment-image">
            {mediaLoading ? (
              <div className="media-loading">Ładowanie zdjęć...</div>
            ) : getCurrentImage()?.file_url ? (
              <img
                src={getCurrentImage()?.file_url}
                alt={getCurrentImage()?.alt_text || 'Rzut mieszkania'}
              />
            ) : (
              <svg className="apartment-image-placeholder" viewBox="0 0 120 100" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="5" y="5" width="110" height="90" rx="4" />
                <line x1="5" y1="40" x2="60" y2="40" />
                <line x1="60" y1="5" x2="60" y2="60" />
                <line x1="60" y1="60" x2="115" y2="60" />
                <rect x="20" y="50" width="25" height="20" rx="2" strokeDasharray="3 2" />
                <rect x="70" y="15" width="30" height="18" rx="2" strokeDasharray="3 2" />
              </svg>
            )}
            {property.priceBadge?.isVisible && (
              <div
                className="promo-badge"
                style={{ background: property.priceBadge.backgroundColor, color: property.priceBadge.textColor }}
              >
                {property.priceBadge.label}
              </div>
            )}
            {propertyMedia.length > 1 && (
              <>
                <div className="image-navigation">
                  <button className="nav-button prev" onClick={handlePrevImage}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button className="nav-button next" onClick={handleNextImage}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <div className="image-counter">
                  {currentImageIndex + 1} / {propertyMedia.length}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Actions section */}
        <div className="actions-section">
          <button
            className="action-button"
            onClick={() => {
              if (property?.media?.card_image) {
                window.open(property.media.card_image, '_blank');
              }
            }}
            disabled={!property?.media?.card_image}
          >
            {investmentData?.property_card_button_label || 'Pobierz kartę lokalu'}
          </button>
          <button
            className="action-button"
            onClick={() => {
              if (property?.media?.image_2d) {
                window.open(property.media.image_2d, '_blank');
              }
            }}
            disabled={!property?.media?.image_2d}
          >
            {investmentData?.view_2d_button_label || 'Zobacz widok 2D'}
          </button>
        </div>

        {/* Contact section */}
        <div className="contact-section">
          <div className="contact-text">
            <div className="contact-question">Masz pytania?</div>
            <div className="contact-action">Skontaktuj się z nami</div>
          </div>
          <div className="contact-buttons">
            {property?.contact?.agent_whatsapp && (
              <button className="contact-button" onClick={handleWhatsAppContact}>
                <img src={imgWarstwa2} alt="WhatsApp" />
              </button>
            )}
            <button className="contact-button" onClick={handlePhoneContact}>
              <img src={imgWarstwa3} alt="Telefon" />
            </button>
            <button className="contact-button" onClick={handleEmailContact}>
              <img src={imgWarstwa4} alt="Email" />
            </button>
          </div>
        </div>

        {/* Navigation section */}
        <div className="property-navigation">
          <button className="nav-property-button prev" onClick={handleNavigateToPrevious}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{property.propertyType === 'lokal uslugowy' ? 'Poprzedni lokal' : 'Poprzednie mieszkanie'}</span>
          </button>
          <button className="nav-property-button next" onClick={handleNavigateToNext}>
            <span>{property.propertyType === 'lokal uslugowy' ? 'Następny lokal' : 'Następne mieszkanie'}</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Footer */}
        <Footer selectedProperty={property} />
      </main>
    </div>
  );
}